import axios from "axios";
import config from "../config";
import store from "../store";
import { updateToken, logout } from "../store/authUserSlice";
import {
  getMetadata,
  saveMetadata,
  getCachedFilterOptions,
  saveCachedFilterOptions,
} from "../web/utils/offlineDB";

const api = axios.create({
  baseURL: `${config.apiUrl}`,
  withCredentials: true,
  timeout: 600000, // 10 minutes timeout
});

// Flag to avoid multiple refresh requests
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newAccessToken) {
  refreshSubscribers.forEach((callback) => callback(newAccessToken));
  refreshSubscribers = [];
}

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

api.interceptors.request.use((req) => {
  const state = store.getState();
  // SECURITY: Get token from Redux state only (memory), never from localStorage
  const token = state.authUser?.userInfo?.token;
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  // Identify requests originating from the Ensaras portal frontend.
  // The backend uses this to bypass API-endpoint restrictions (APIAccess)
  // which are only meant for direct API consumers.
  req.headers['X-Ensaras-Client'] = 'portal';
  return req;
});

// Response interceptor for auto-refresh and global unwrapping
api.interceptors.response.use(
  (response) => {
    // Automatically unwrap the standard API response format `{ success, code, message, data }`
    if (
      response.data &&
      typeof response.data === 'object' &&
      'success' in response.data &&
      'code' in response.data &&
      'message' in response.data &&
      'data' in response.data
    ) {
      const originalData = response.data;
      
      // Re-assign data so components transparently get the payload (e.g. array of users)
      response.data = originalData.data;

      // Preserve top-level metadata on the response object just in case the UI needs it
      response._apiSuccess = originalData.success;
      response._apiMessage = originalData.message;
      response._apiCode = originalData.code;

      // Backwards Compatibility Hacks:
      // 1. Some django-ninja components explicitly unpacked `response.data.data`.
      // By adding a non-enumerable getter, `response.data.data` returns itself safely!
      if (response.data && typeof response.data === 'object' && !('data' in response.data)) {
        Object.defineProperty(response.data, 'data', {
          get: function() { return this; },
          enumerable: false
        });
      }
      
      // 2. The login flow historically checked `response.data.success` or `response.data.message`
      // We can safely attach these to the unwrapped object (non-enumerable) if they don't exist
      if (response.data && typeof response.data === 'object') {
        if (!('success' in response.data)) {
          Object.defineProperty(response.data, 'success', { value: originalData.success, enumerable: false });
        }
        if (!('message' in response.data)) {
          Object.defineProperty(response.data, 'message', { value: originalData.message, enumerable: false });
        }
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 403) {
      const { message } = error.response.data;
      if (message === "Unauthorized" || message === "Token has expired") {
        store.dispatch(logout());
        localStorage.clear();
        localStorage.clear(); // Clean up any legacy storage
        window.location.href = "/signin";
      }
      return Promise.reject(error);
    }

    // Handle 401 - attempt refresh
    if (error.response && error.response.status === 401 && error.response?.data?.code === "token_not_valid" && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        // Call Django JWT refresh endpoint
        // FIXED: Correct endpoint is /api/refresh/ (not /api/token/refresh/)
        const response = await axios.post(
          `${config.apiUrl}/refresh/`,
          {}, // No body needed, refresh_token is in httpOnly cookie
          { withCredentials: true }
        );

        // FIXED: Backend returns { data: { token: "..." } } inside response.data
        const newAccessToken = response.data?.data?.token || response.data?.token;

        // SECURITY: Store token in Redux state only, never in localStorage
        store.dispatch(updateToken(newAccessToken));
        api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
        onRefreshed(newAccessToken);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        store.dispatch(logout());
        localStorage.clear();
        localStorage.clear(); // Clean up any legacy storage
        window.location.href = "/signin";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    if (error.response && error.response.status === 401) {
      store.dispatch(logout());
      localStorage.clear();
      localStorage.clear(); // Clean up any legacy storage
      // Avoid redirection on same page
      const current = window.location.pathname;
      if (current !== "/signin") {
        window.location.href = "/signin";
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Intercept get calls for metadata and filter options to support offline mode
const originalGet = api.get;
api.get = async function (url, config) {
  const isMetadata = url && url.includes("/metadata/");
  const isFilterOptions = url && url.includes("/filter-options/");

  if (isMetadata || isFilterOptions) {
    // Extract formId
    let formId = null;
    if (config && config.params && config.params.form_id) {
      formId = config.params.form_id;
    } else if (config && config.params && config.params.formId) {
      formId = config.params.formId;
    } else if (url) {
      const match = url.match(/[?&]form_id=([^&]+)/) || url.match(/[?&]formId=([^&]+)/);
      if (match) {
        formId = match[1];
      }
    }

    // Extract field parameter for filter options
    let field = null;
    if (isFilterOptions) {
      if (config && config.params && config.params.field) {
        field = config.params.field;
      } else if (url) {
        const fieldMatch = url.match(/[?&]field=([^&]+)/);
        if (fieldMatch) {
          field = fieldMatch[1];
        }
      }
    }

    const canCache = isMetadata ? !!formId : (!!formId && !!field);

    if (canCache) {
      // If we are offline, attempt fallback immediately
      if (!navigator.onLine) {
        try {
          const cached = isMetadata
            ? await getMetadata(formId)
            : await getCachedFilterOptions(formId, field);

          if (cached) {
            console.log(`[Offline API] Returning cached ${isMetadata ? "metadata" : "filter options"} for form ${formId}`);
            return {
              data: cached,
              status: 200,
              statusText: "OK",
              headers: {},
              config,
            };
          }
        } catch (err) {
          console.error("Error reading from IndexedDB cache in offline mode:", err);
        }
      }

      // If we are online, try network request first
      try {
        const response = await originalGet.call(api, url, config);
        
        // Save to cache on success
        if (response && response.data) {
          if (isMetadata) {
            await saveMetadata(formId, response.data);
          } else {
            await saveCachedFilterOptions(formId, field, response.data);
          }
        }
        return response;
      } catch (error) {
        // If network request failed (e.g. server error, network timeout), fallback to cache
        console.warn("Network request failed, attempting cache fallback:", error);
        try {
          const cached = isMetadata
            ? await getMetadata(formId)
            : await getCachedFilterOptions(formId, field);

          if (cached) {
            console.log(`[Offline API Fallback] Returning cached ${isMetadata ? "metadata" : "filter options"} for form ${formId}`);
            return {
              data: cached,
              status: 200,
              statusText: "OK",
              headers: {},
              config,
            };
          }
        } catch (cacheErr) {
          console.error("Error reading from cache during fallback:", cacheErr);
        }
        // If no cache or cache failed, throw original error
        throw error;
      }
    }
  }

  // For other endpoints or if formId is not found, proceed normally
  return originalGet.call(api, url, config);
};

export default api;
